import { Divider, Drawer, Flex, Layout, Button, Dropdown, Typography } from 'antd'
import { RightOutlined, LeftOutlined } from '@ant-design/icons'
import { Pto } from '@outcoreaix/pto'
import { useIsLoggedIn } from 'app/use-is-logged-in'
import { FC, useEffect, useMemo, useState } from 'react'
import UserMenuItem from '../UserMenuItem'
import MenuList from '../MenuList'
import { useLocation } from 'react-router-dom'
import { useWorkspace } from 'hooks/useWorkspace'
import { useWorkspaceLogoSize } from 'hooks/useWorkspaceLogoSize'
import { useChatMenu } from 'hooks/useChatMenu'
import { useIsDesktop } from 'hooks/useIsDesktop'
import SettingsMenu from '../SettingsMenu'
import { authStorage } from 'app/auth-storage'
import { WorkspaceMenu } from '../WorkspaceMenu'
import { ChatMenuContent } from '../../../features/chats/components/ChatMenu'
import { WhatsNewToolbarButton } from 'features/changelog/components/WhatsNewToolbarButton'
import { TOUR_SET_NAV_COLLAPSED_EVENT, TOUR_TARGET_IDS } from 'features/spaces/components/LearnerProductTour'

type Props = {
  open: boolean
  onClose: () => void
  hasSubMenu?: boolean
}

const NavMenu: FC<Props> = ({ open, onClose, hasSubMenu = false }) => {
  const isLoggedIn = useIsLoggedIn()
  const { currentWorkspace, currentWorkspaceId, workspaces } = useWorkspace()
  const location = useLocation()
  const [isCollapsed, setIsCollapsed] = useState(true)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const isDesktop = useIsDesktop()
  const user = authStorage.user
  const userInitials = user ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase() : ''

  const { width: logoWidth } = useWorkspaceLogoSize(isCollapsed)
  const headerShift = Math.max(0, (logoWidth - 16) / 2)

  const isSettingsPage = useMemo(
    () => location.pathname.includes('settings') && !location.pathname.includes('app-settings'),
    [location.pathname]
  )

  // The product tour lives in the HomePage tree but needs the sidebar expanded to highlight
  // the chat history; it drives this collapsed state through a CustomEvent.
  useEffect(() => {
    const onSetCollapsed = (e: Event) => setIsCollapsed((e as CustomEvent<{ collapsed: boolean }>).detail.collapsed)
    window.addEventListener(TOUR_SET_NAV_COLLAPSED_EVENT, onSetCollapsed)
    return () => window.removeEventListener(TOUR_SET_NAV_COLLAPSED_EVENT, onSetCollapsed)
  }, [])

  if (!isLoggedIn) {
    return null
  }

  const { chats, activeChatId, isCreateChatLoading, switchChats, handleCreateChat, handleDeleteChat, isAIConfigSet } =
    useChatMenu()

  return (
    <>
      <Layout.Sider
        key={`${location.pathname}_desktop`}
        breakpoint="xl"
        trigger={null}
        collapsedWidth={isSettingsPage ? 392 : 92}
        theme="light"
        collapsed={isCollapsed}
        width={isSettingsPage ? 600 : 300}
        className="max-xl:hidden"
      >
        <Flex vertical justify="space-between" className="h-full">
          <Layout>
            <Layout.Sider
              breakpoint="xl"
              theme="light"
              trigger={null}
              collapsedWidth={92}
              width={300}
              className="p-6 max-xl:hidden h-dvh xl:h-screen border-r-menu border-r [&_.ant-layout-sider-children]:flex [&_.ant-layout-sider-children]:flex-col [&_.ant-layout-sider-children]:justify-between"
              collapsed={isCollapsed}
            >
              <Flex vertical className="flex-1 min-h-0">
                <Flex
                  align="center"
                  justify="space-between"
                  gap={4}
                  className="w-full"
                  style={
                    isCollapsed
                      ? {
                          width: `calc(100% + ${headerShift * 2}px)`,
                          marginLeft: -headerShift,
                          marginRight: -headerShift
                        }
                      : undefined
                  }
                >
                  {!!workspaces?.length && !user?.roles.includes(Pto.Users.UserRole.SystemAdmin) && (
                    <WorkspaceMenu isCollapsed={isCollapsed} />
                  )}
                  <Button
                    type="text"
                    icon={isCollapsed ? <RightOutlined /> : <LeftOutlined />}
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    className="!w-6 !min-w-6 h-10 !p-0 ml-auto flex items-center justify-center hover:bg-gray-100 flex-shrink-0"
                  />
                </Flex>
                <Divider type="horizontal" className="mx-0 my-4 2-full !border-menu" />
                <MenuList isDesktop collapsed={isCollapsed} />
                <Divider className="my-3.5" />
                <ChatMenuContent
                  // Both the desktop and mobile chat menus stay mounted (the desktop one is hidden via
                  // `max-xl:hidden`, not unmounted), so the tour's chat-history anchor id must live only
                  // on the currently-visible instance — otherwise getElementById would resolve the
                  // hidden duplicate. It moves between instances reactively as the viewport crosses xl.
                  id={isDesktop ? TOUR_TARGET_IDS.chatHistory : undefined}
                  chats={chats}
                  disabled={!isAIConfigSet}
                  onChatClick={(id) => switchChats(id)}
                  isLoading={isCreateChatLoading}
                  createChat={handleCreateChat}
                  deleteChat={handleDeleteChat}
                  activeChatId={activeChatId}
                  isCollapsed={isCollapsed}
                />
              </Flex>
              <Flex vertical>
                <WhatsNewToolbarButton collapsed={isCollapsed} />
                <Divider className="mx-0 my-4 2-full !border-menu" />
                <Dropdown
                  open={isUserMenuOpen && isDesktop}
                  onOpenChange={setIsUserMenuOpen}
                  trigger={['click']}
                  overlayClassName="user-menu-dropdown"
                  dropdownRender={() => (
                    <div className="absolute left-0 bottom-0 bg-white border border-gray-300 rounded-lg shadow-lg p-4 min-w-[275px] z-50">
                      <UserMenuItem
                        collapsed={hasSubMenu}
                        workspace={currentWorkspace}
                        currentWorkspaceId={currentWorkspaceId}
                        onMenuItemClick={() => setIsUserMenuOpen(false)}
                        isDesktop={true}
                      />
                    </div>
                  )}
                >
                  <Flex gap={8} className="items-center">
                    <Button className="user-initials-btn" onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}>
                      {userInitials}
                    </Button>
                    {!isCollapsed && (
                      <Typography.Text className="text-sm whitespace-nowrap text-ellipsis overflow-hidden">
                        {user?.email}
                      </Typography.Text>
                    )}
                  </Flex>
                </Dropdown>
              </Flex>
            </Layout.Sider>
            {isSettingsPage && (
              <Layout.Sider collapsed={false} width={300}>
                <SettingsMenu />
                <Divider type="vertical" className="mx-0 h-full !border-menu" />
              </Layout.Sider>
            )}
          </Layout>
        </Flex>
      </Layout.Sider>
      <Drawer
        key={`${location.pathname}_drawer`}
        classNames={{
          header: '[&>div>button]:!-ml-1 !py-4 [&_.ant-drawer-header-title]:!flex-row-reverse',
          body: '!pb-0'
        }}
        onClose={onClose}
        open={open}
        placement="left"
        className="xl:hidden"
        width={300}
        // Keep the drawer content mounted while closed so the tour's chat-history anchor exists in the
        // DOM when buildSteps() runs at tour open (it filters out steps whose target is absent).
        forceRender
      >
        <Flex vertical justify="space-between" className="h-full">
          <MenuList key={`${location.pathname}_mobile`} onClick={onClose} isDesktop={false} />
          <Divider className="my-3.5" />
          <ChatMenuContent
            // On mobile this is the visible chat menu, so it carries the tour's chat-history anchor
            // (the desktop instance drops it). See the desktop ChatMenuContent above.
            id={!isDesktop ? TOUR_TARGET_IDS.chatHistory : undefined}
            chats={chats}
            disabled={!isAIConfigSet}
            onChatClick={(id) => {
              switchChats(id)
              onClose?.()
            }}
            isLoading={isCreateChatLoading}
            createChat={handleCreateChat}
            deleteChat={handleDeleteChat}
            activeChatId={activeChatId}
            isCollapsed={false}
          />
          <Flex vertical className="pb-4">
            <WhatsNewToolbarButton onNavigate={onClose} />
            {!!workspaces?.length && !user?.roles.includes(Pto.Users.UserRole.SystemAdmin) && (
              <WorkspaceMenu onWorkspaceClick={onClose} />
            )}
            <Divider className="mx-0 my-4 2-full !border-menu" />
            <Dropdown
              // See the desktop dropdown above: gate on !isDesktop so only the mobile dropdown is active
              // here, and the always-mounted desktop dropdown can't hijack the shared open state.
              open={isUserMenuOpen && !isDesktop}
              onOpenChange={setIsUserMenuOpen}
              trigger={['click']}
              overlayClassName="user-menu-dropdown"
              dropdownRender={() => (
                <div className="absolute left-0 bottom-0 bg-white border border-gray-300 rounded-lg shadow-lg p-4 min-w-[275px] z-50 pointer-events-auto">
                  <UserMenuItem
                    collapsed={hasSubMenu}
                    workspace={currentWorkspace}
                    currentWorkspaceId={currentWorkspaceId}
                    onMenuItemClick={() => {
                      onClose?.()
                      setIsUserMenuOpen(false)
                    }}
                    isDesktop={false}
                  />
                </div>
              )}
            >
              <Flex gap={8} className="items-center">
                <Button className="user-initials-btn" onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}>
                  {userInitials}
                </Button>
                <Typography.Text className="text-sm whitespace-nowrap text-ellipsis overflow-hidden">
                  {user?.email}
                </Typography.Text>
              </Flex>
            </Dropdown>
          </Flex>
        </Flex>
      </Drawer>
    </>
  )
}

export default NavMenu
